import * as React from "react"
import { Search, X } from "lucide-react"
import { cn } from "@verlo/shared/lib/utils"

/**
 * A search box: the magnifier and the text as one field, and the whole field takes the
 * focus ring (Back office sweep, Oct 2026). Pages built it as a bordered box with a
 * borderless input inside, so the ring lit up the bare input, inside the box.
 *
 *   <SearchField placeholder="Search reports" value={q} onChange={(e) => setQ(e.target.value)} />
 *
 * `className` sizes and places the field; `onClear` adds a clear button while there's text
 * (`clearTestId` names it); everything else goes to the input.
 */
const SearchField = React.forwardRef(({ className, size = "md", onClear, clearTestId, ...props }, ref) => (
  <label
    className={cn(
      "flex w-full items-center gap-2 rounded-md border border-input bg-white px-3 text-ink-faint transition-colors",
      "focus-within:border-verlo focus-within:ring-1 focus-within:ring-inset focus-within:ring-verlo",
      size === "sm" ? "h-8" : "h-9",
      className,
    )}
  >
    <Search size={size === "sm" ? 13 : 15} aria-hidden className="shrink-0" />
    <input
      ref={ref}
      type="search"
      className={cn(
        "h-full min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-ink-faint",
        "[&::-webkit-search-cancel-button]:appearance-none",
        size === "sm" ? "text-[12.5px]" : "text-sm",
      )}
      {...props}
    />
    {onClear && props.value ? (
      <button type="button" onClick={onClear} aria-label="Clear the search" className="shrink-0 text-ink-faint hover:text-ink" data-testid={clearTestId}>
        <X size={13} />
      </button>
    ) : null}
  </label>
))
SearchField.displayName = "SearchField"

export { SearchField }
